import { tokenManager } from "utils/tokenManager";

// Allow overriding the API base via env; default to local API used in curl examples
const API_BASE = process.env.REACT_APP_API_BASE || "https://usibras-web-backend.onrender.com/api";

/** Simple cache for list endpoint (TTL: 2 minutes) */
const CACHE_TTL_MS = 2 * 60 * 1000;
let cache = { data: null, timestamp: 0 };

function isCacheValid() {
  return cache.data !== null && Date.now() - cache.timestamp < CACHE_TTL_MS;
}

export function invalidateRcnReturnsCache() {
  cache = { data: null, timestamp: 0 };
}

/**
 * Fetch return records from server.
 * Uses cache unless forceRefresh is true.
 */
export async function fetchRcnReturns(forceRefresh = false) {
  if (!forceRefresh && isCacheValid()) return cache.data;

  const authHeader = tokenManager.getAuthHeader();
  const resp = await fetch(`${API_BASE}/rcn-returns`, {
    headers: authHeader ? { Authorization: authHeader } : undefined,
  });
  const result = await resp.json().catch(() => ({}));

  if (!resp.ok) {
    throw new Error(result.message || result.error || 'Failed to fetch rcn returns');
  }

  const data = result.data || result || [];
  cache = { data, timestamp: Date.now() };
  return data;
}

/**
 * Create a new return record.
 * payload should match backend API, e.g. include: date, rcn_type, moisture, origin, outturn, size, quantity, zone, real_weight, number_of_bags
 */
export async function createRcnReturn(payload) {
  const authHeader = tokenManager.getAuthHeader();

  const resp = await fetch(`${API_BASE}/rcn-returns`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authHeader ? { Authorization: authHeader } : {}),
    },
    body: JSON.stringify(payload),
  });

  const result = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(result.message || result.error || 'Failed to create rcn return');
  }

  // Invalidate cache so next fetch gets fresh data
  invalidateRcnReturnsCache();
  return result;
}

/**
 * Update an existing return record.
 * payload must include `id` plus the fields to update, e.g.:
 * { id, date, rcn_type, moisture, origin, outturn, size, quantity, zone, real_weight, number_of_bags }
 */
export async function updateRcnReturn(payload) {
  const authHeader = tokenManager.getAuthHeader();

  const resp = await fetch(`${API_BASE}/rcn-returns`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(authHeader ? { Authorization: authHeader } : {}),
    },
    body: JSON.stringify(payload),
  });

  const result = await resp.json().catch(() => ({}));
  if (!resp.ok || result.success === false) {
    throw new Error(result.message || result.error || 'Failed to update rcn return');
  }

  invalidateRcnReturnsCache();
  return result;
}

/**
 * Delete a return record by id.
 */
export async function deleteRcnReturn(id) {
  const authHeader = tokenManager.getAuthHeader();

  const resp = await fetch(`${API_BASE}/rcn-returns/${id}`, {
    method: 'DELETE',
    headers: authHeader ? { Authorization: authHeader } : undefined,
  });

  const result = await resp.json().catch(() => ({}));
  if (!resp.ok || result.success === false) {
    throw new Error(result.message || result.error || 'Failed to delete rcn return');
  }

  invalidateRcnReturnsCache();
  return result;
}
